import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { sectionAlignment } from "../data/projects";

const filterMap = {
  none: "",
  grayscale: "grayscale-[100%] contrast-[1.05]",
  warm: "sepia-[0.18] saturate-[1.05] contrast-[1.02]",
  cool: "saturate-[0.85] contrast-[1.08] hue-rotate-[-8deg]"
};

export default function PortfolioSection({ project, index, total, onMediaClick, displayTitle, editable, onTitleSave }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const fullVideoRef = useRef(null);
  const [fullReady, setFullReady] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.4, 0.6, 1], [0, 1, 1, 0]);
  // Title parallax — moves a bit faster than the image and lags behind the scroll
  // by about half a second for a soft, editorial trail.
  const titleYRaw = useTransform(scrollYProgress, [0, 1], ["60px", "-60px"]);
  const titleY = useSpring(titleYRaw, { stiffness: 55, damping: 18, mass: 1.2 });

  // iOS Safari refuses to autoplay <video> in some states even when muted +
  // playsInline + autoplay are all set declaratively. Force-mute imperatively
  // and call .play() once after mount; ignore the promise rejection so the
  // poster keeps showing if the browser ultimately blocks it. Run this for
  // both the lightweight preview video AND the full video.
  useEffect(() => {
    const refs = [videoRef.current, fullVideoRef.current].filter(Boolean);
    if (refs.length === 0) return;
    const handlers = [];
    refs.forEach((v) => {
      v.muted = true;
      v.defaultMuted = true;
      v.setAttribute("muted", "");
      v.setAttribute("playsinline", "");
      v.setAttribute("webkit-playsinline", "");
      const tryPlay = () => {
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      };
      tryPlay();
      v.addEventListener("loadedmetadata", tryPlay, { once: true });
      v.addEventListener("canplay", tryPlay, { once: true });
      handlers.push({ v, tryPlay });
    });
    return () => {
      handlers.forEach(({ v, tryPlay }) => {
        v.removeEventListener("loadedmetadata", tryPlay);
        v.removeEventListener("canplay", tryPlay);
      });
    };
    // Mount-only on purpose: videoRef / fullVideoRef are refs (stable across
    // renders) and we want this priming sequence to run exactly once. The
    // parent re-keys this component by project.slug so refs are always fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a lightweight preview is provided, only flag the full video as ready
  // (and fade it in) once it has enough buffered data to play without stalling.
  useEffect(() => {
    const full = fullVideoRef.current;
    if (!full || !project.media?.preview) return;
    const onReady = () => setFullReady(true);
    if (full.readyState >= 4) {
      onReady();
      return;
    }
    full.addEventListener("canplaythrough", onReady, { once: true });
    return () => full.removeEventListener("canplaythrough", onReady);
    // `full`, `onReady` are effect-scoped locals; fullVideoRef is a ref
    // (stable); setFullReady is a stable setter — none belong in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.media?.preview]);

  const alignClasses =
    index === 0
      ? "items-center justify-end text-right pr-8 md:pr-16"
      : sectionAlignment(index);
  const num = String(index + 1).padStart(2, "0");
  const totalStr = String(total).padStart(2, "0");
  const isVideo = project.media.type === "video";
  const clickable = Boolean(project.slug && onMediaClick);

  const handleMediaClick = () => {
    if (!clickable) return;
    onMediaClick(project, index);
  };

  return (
    <section
      ref={ref}
      id={`section-${num}`}
      data-testid={`portfolio-section-${num}`}
      className="portfolio-snap relative w-full h-screen overflow-hidden bg-black"
    >
      {/* Media layer with parallax (clickable for case-study sections) */}
      <motion.div
        style={{ y }}
        className={`absolute inset-[-22%] will-change-transform ${clickable ? "cursor-pointer" : ""}`}
        onClick={handleMediaClick}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={(e) => {
          if (clickable && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleMediaClick();
          }
        }}
        data-testid={clickable ? `section-${num}-media-clickable` : undefined}
        aria-label={clickable ? `Open ${project.title} case study` : undefined}
      >
        {isVideo ? (
          project.media.preview ? (
            <>
              {/* Lightweight preview — paints instantly (~100 KB) */}
              <video
                ref={videoRef}
                data-testid={`section-${num}-video-preview`}
                poster={project.media.poster}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                disableRemotePlayback
                controls={false}
                className={`absolute inset-0 w-full h-full object-cover pointer-events-none ${filterMap[project.filter] || ""}`}
              >
                <source src={project.media.preview.webm} type="video/webm" />
                <source src={project.media.preview.mp4} type="video/mp4" />
              </video>
              {/* Full-res video — fades in once canplaythrough */}
              <video
                ref={fullVideoRef}
                data-testid={`section-${num}-video`}
                src={project.media.src}
                poster={project.media.poster}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                disableRemotePlayback
                controls={false}
                style={{ opacity: fullReady ? 1 : 0, transition: "opacity 600ms ease" }}
                className={`absolute inset-0 w-full h-full object-cover pointer-events-none ${filterMap[project.filter] || ""}`}
              />
            </>
          ) : (
            <video
              ref={videoRef}
              data-testid={`section-${num}-video`}
              src={project.media.src}
              poster={project.media.poster}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              disableRemotePlayback
              controls={false}
              className={`w-full h-full object-cover pointer-events-none ${filterMap[project.filter] || ""}`}
            />
          )
        ) : (
          <img
            data-testid={`section-${num}-image`}
            src={project.media.src}
            alt={project.title}
            loading={index < 2 ? "eager" : "lazy"}
            className={`w-full h-full object-cover pointer-events-none ${filterMap[project.filter] || ""}`}
          />
        )}
      </motion.div>

      {/* Readability scrim — gradient direction follows where the caption sits. */}
      {(() => {
        let scrimClass = "bg-black/25";
        if (alignClasses.includes("items-end")) {
          scrimClass = "bg-gradient-to-t from-black/70 via-black/15 to-transparent";
        } else if (alignClasses.includes("items-start")) {
          scrimClass = "bg-gradient-to-b from-black/65 via-black/10 to-transparent";
        }
        return <div className={`absolute inset-0 pointer-events-none ${scrimClass}`} />;
      })()}

      <div className="grain absolute inset-0 pointer-events-none" />

      {/* Foreground caption */}
      <motion.div
        style={{ opacity: overlayOpacity, y: titleY }}
        className={`relative z-10 w-full h-full flex pointer-events-none ${alignClasses}`}
      >
        <motion.div
          className="max-w-xl text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <h2
            data-testid={`section-${num}-title`}
            data-portfolio-headline
            contentEditable={editable ? true : undefined}
            suppressContentEditableWarning
            spellCheck={false}
            onClick={editable ? (e) => e.stopPropagation() : undefined}
            onKeyDown={
              editable
                ? (e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }
                : undefined
            }
            onBlur={
              editable && onTitleSave
                ? (e) => {
                    const next = (e.currentTarget.textContent || "").trim();
                    if (next && next !== (displayTitle || project.title)) {
                      onTitleSave(index, next);
                    } else {
                      e.currentTarget.textContent = displayTitle || project.title;
                    }
                  }
                : undefined
            }
            className={`font-display text-[1.65rem] md:text-[clamp(1.1rem,2.8vw,2.4rem)] leading-[0.95] tracking-[-0.02em] font-medium outline-none ${
              editable
                ? "pointer-events-auto cursor-text rounded-sm px-1 -mx-1 ring-1 ring-white/30 hover:ring-white/60 focus:ring-white/90 transition-shadow"
                : ""
            }`}
          >
            {displayTitle || project.title}
          </h2>
        </motion.div>
      </motion.div>
    </section>
  );
}
